/**
 * Sorting utilities for Analytics component
 */

import { ChartDataset } from '../_interfaces/analytics.interfaces';

export type SortDirection = 'asc' | 'desc';

interface SortableItem {
  label: string;
  value: number;
  compareValue?: number;
  index: number;
}

/**
 * Sort bar chart data by value
 */
export function sortBarChartData(
  labels: string[],
  datasets: ChartDataset[],
  compareData: number[] | undefined,
  sortDirection: SortDirection,
  hasCompare: boolean = false
): { labels: string[]; datasets: ChartDataset[]; compareData: number[] } {
  if (!labels || !datasets || datasets.length === 0) {
    return { labels: labels || [], datasets, compareData: compareData || [] };
  }

  const data = datasets[0].data as number[];
  if (!data || data.length !== labels.length) {
    return { labels, datasets, compareData: compareData || [] };
  }

  // Create array of {label, value, compareValue, index} for sorting
  const items: SortableItem[] = labels.map((label, index) => ({
    label,
    value: data[index] || 0,
    compareValue: compareData && compareData[index] ? compareData[index] : 0,
    index
  }));

  // Sort by value
  items.sort((a, b) => {
    if (sortDirection === 'desc') {
      return b.value - a.value;
    } else {
      return a.value - b.value;
    }
  });

  // Update labels and data arrays
  const sortedLabels = items.map(item => item.label);
  const sortedData = items.map(item => item.value);
  const sortedCompareData = items.map(item => item.compareValue || 0);

  // Update chart data
  const updatedDatasets = [...datasets];
  updatedDatasets[0] = {
    ...updatedDatasets[0],
    data: sortedData
  };

  if (hasCompare && updatedDatasets.length > 1) {
    updatedDatasets[1] = {
      ...updatedDatasets[1],
      data: sortedCompareData
    };
  }

  return {
    labels: sortedLabels,
    datasets: updatedDatasets,
    compareData: sortedCompareData
  };
}

/**
 * Limit bar chart data to a specific number of items
 * @param fromEnd If true, take items from the end of the array (for bottom N), otherwise take from the start (for top N)
 */
export function limitBarChartData(
  labels: string[],
  datasets: ChartDataset[],
  compareData: number[] | undefined,
  limit: number,
  fromEnd: boolean = false
): { labels: string[]; datasets: ChartDataset[]; compareData: number[] } {
  if (!labels || !datasets || datasets.length === 0 || limit <= 0) {
    return { labels: labels || [], datasets, compareData: compareData || [] };
  }

  // If limit is greater than or equal to the number of items, return as-is
  if (limit >= labels.length) {
    return { labels, datasets, compareData: compareData || [] };
  }

  // Limit labels
  const limitedLabels = fromEnd
    ? labels.slice(-limit)
    : labels.slice(0, limit);

  // Limit datasets
  const limitedDatasets = datasets.map(dataset => {
    if (!dataset.data || !Array.isArray(dataset.data)) {
      return dataset;
    }
    const datasetData = dataset.data as number[];
    return {
      ...dataset,
      data: fromEnd
        ? datasetData.slice(-limit)
        : datasetData.slice(0, limit)
    };
  });

  // Limit compare data
  const limitedCompareData = compareData
    ? (fromEnd ? compareData.slice(-limit) : compareData.slice(0, limit))
    : [];

  return {
    labels: limitedLabels,
    datasets: limitedDatasets,
    compareData: limitedCompareData
  };
}

/**
 * Sort stacked bar chart data by total across all datasets
 * @param filterByCurrentStack If true, only include datasets with stack === 'current'
 */
export function sortStackedBarChartData(
  labels: string[],
  datasets: ChartDataset[],
  sortDirection: SortDirection,
  filterByCurrentStack: boolean = true
): { labels: string[]; datasets: ChartDataset[] } {
  if (!labels || !datasets || datasets.length === 0) {
    return { labels: labels || [], datasets };
  }

  // For single dataset, use simple bar chart sorting
  if (datasets.length === 1) {
    const result = sortBarChartData(labels, datasets, undefined, sortDirection, false);
    return {
      labels: result.labels,
      datasets: result.datasets
    };
  }

  // Filter datasets if needed
  const datasetsToUse = filterByCurrentStack
    ? datasets.filter(dataset => {
        if (!dataset) {
          return false;
        }
        if (!dataset.stack) {
          return true;
        }
        return dataset.stack === 'current';
      })
    : datasets;

  if (datasetsToUse.length === 0 || !datasetsToUse[0].data || !Array.isArray(datasetsToUse[0].data)) {
    return { labels, datasets };
  }

  const firstData = datasetsToUse[0].data as number[];
  if (!firstData || firstData.length !== labels.length) {
    return { labels, datasets };
  }

  // Calculate totals for each label across all datasets
  const items: SortableItem[] = labels.map((label, index) => {
    const total = datasetsToUse.reduce((sum, dataset) => {
      if (!dataset.data || !Array.isArray(dataset.data)) {
        return sum;
      }
      const datasetData = dataset.data as number[];
      return sum + (datasetData[index] || 0);
    }, 0);
    return { label, value: total, index };
  });

  // Sort by total value
  items.sort((a, b) => {
    if (sortDirection === 'desc') {
      return b.value - a.value;
    } else {
      return a.value - b.value;
    }
  });

  // Update labels
  const sortedLabels = items.map(item => item.label);

  // Update each dataset's data array
  const updatedDatasets = datasets.map(dataset => {
    if (!dataset.data || !Array.isArray(dataset.data)) {
      return dataset;
    }
    const datasetData = dataset.data as number[];
    const sortedData = items.map(item => {
      if (!datasetData || datasetData.length <= item.index) {
        return 0;
      }
      return datasetData[item.index] || 0;
    });
    return {
      ...dataset,
      data: sortedData
    };
  });

  return {
    labels: sortedLabels,
    datasets: updatedDatasets
  };
}
